import { Component, OnInit } from '@angular/core';
import { SeoService } from '@app/core/seo/seo.service';
import { ensureLottiePlayer } from '@app/core/lottie/lottie-loader';
import { ActivatedRoute } from '@angular/router';
import { AbstractControl, NgForm } from '@angular/forms';
import { ConfigService } from 'esz-saas-lcp-ng-crud-ui';
import { ContactService, ContactTopic } from './contact.service';

@Component({
    standalone: false,
    selector: 'app-ws-contact',
    templateUrl: './ws-contact.component.html',
    styleUrls: ['./ws-contact.component.scss']
})
export class WsContactComponent implements OnInit {
    topics: { value: ContactTopic; label: string }[] = [
        { value: 'general', label: 'General enquiry' },
        { value: 'snapcart', label: 'Snapcart local edition: request licence + account' }
    ];
    model = { topic: 'general' as ContactTopic, name: '', email: '', company: '', os: '', mac: '', message: '' };
    isSubmitting = false;
    showSuccess = false;
    showError = false;
    /** Set when a submit was refused by validation, so the visitor sees why nothing was sent. */
    showInvalid = false;
    /** Topic of the last successful submission, so the success line matches what was sent. */
    submittedSnapcart = false;

    constructor(public configService: ConfigService, private route: ActivatedRoute, private contactService: ContactService, private seo: SeoService) { }

    ngOnInit(): void {
        this.seo.set({
            title: 'Contact',
            description: 'Contact ESZES Consultancy & Services in Thrissur, Kerala: call +91 98950 13318, email info@eszes.in, or send a message or Snapcart access request through the website form.',
            path: '/contact'
        });
        ensureLottiePlayer();
        // Subscribe, not snapshot: the router reuses this component when only the query string
        // changes (/contact?topic=snapcart -> /contact), so a snapshot read would keep the old topic.
        this.route.queryParamMap.subscribe(params => {
            this.model.topic = params.get('topic') === 'snapcart' ? 'snapcart' : 'general';
            // /contact?about=<service title> from a services card pre-fills the opening line.
            const about = params.get('about');
            if (about && !this.model.message) {
                this.model.message = `I would like to talk about: ${about}.\n\n`;
            }
        });
    }

    get isSnapcart(): boolean { return this.model.topic === 'snapcart'; }

    /**
     * Normalise whatever the visitor pasted into the MAC field. Windows tools print `10-FF-E0-EC-8A-F1`
     * and a copy from Command Prompt often carries trailing spaces; both used to fail the pattern silently.
     * Whitespace is dropped, hex is upper-cased and any of `:`, `-`, `.` or no separator becomes `:` pairs.
     * A value that is not 12 hex digits is left alone (minus whitespace) so the validator can reject it.
     * Runs at submit only, through the FormControl: ngModel copies the model into the control on a resolved
     * promise, so a submit that only changed `model.mac` validated the old value and was refused with no
     * visible error; and a `(blur)` normaliser removed the error line under the field on mousedown, moved the
     * button and lost the click. The pattern on the input already tolerates whitespace and missing
     * separators, so this only canonicalises what the validator accepted (found 2026-09-17).
     */
    normalizeMac(ctrl?: AbstractControl | null): void {
        const raw = String((ctrl ? ctrl.value : this.model.mac) || '').replace(/\s+/g, '');
        const hex = raw.replace(/[:\-.]/g, '');
        const value = /^[0-9A-Fa-f]{12}$/.test(hex) ? hex.toUpperCase().match(/.{2}/g).join(':') : raw;
        this.model.mac = value;
        if (ctrl && ctrl.value !== value) { ctrl.setValue(value); }
    }

    onSubmit(form: NgForm): void {
        this.normalizeMac(form.controls['mac']);
        if (form.invalid) { form.control.markAllAsTouched(); this.showInvalid = true; return; }
        this.isSubmitting = true; this.showSuccess = false; this.showError = false; this.showInvalid = false;
        const topic = this.model.topic;
        this.contactService.submit({ ...this.model }).subscribe({
            next: () => {
                this.isSubmitting = false; this.showSuccess = true; this.submittedSnapcart = topic === 'snapcart';
                form.resetForm({ topic });
                this.model.topic = topic;
            },
            error: (err) => { console.error('contact form submission failed', err); this.isSubmitting = false; this.showError = true; }
        });
    }
}
